import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar en reklamfilm i Sverige?",
    a: "Spannet är brett: en enklare social-first reklamfilm landar typiskt på 35 000–120 000 kr, medan en TV-färdig kampanjfilm med skådespelare och flera versioner ligger på 250 000–900 000 kr. Avgörande är inspelningsdagar, antal versioner och om vi använder originalmusik. Med Fenny Pulse får ni reklamfilm månadsvis från en fast budget istället för per projekt.",
  },
  {
    q: "Hur lång ska en reklamfilm vara?",
    a: "För paid social (Meta, TikTok, YouTube Shorts) ligger sweet spot på 9–22 sekunder. För YouTube pre-roll fungerar 15 eller 30 sekunder bäst. För sajt och employer branding kan långformat på 60–180 sekunder bära. Vi rekommenderar att skära varje produktion i 4–6 längder per insats.",
  },
  {
    q: "Hur lång tid tar det att producera en reklamfilm?",
    a: "Från brief till leverans: 3–6 veckor för en standardproduktion. Pulse-abonnemangskunder får sin första film inom 10–14 dagar eftersom strategin redan är gjord. Komplex produktion med skådespelare och location-resor: 8–12 veckor.",
  },
  {
    q: "Vilka format och kanaler levererar ni för?",
    a: "Vi levererar alla relevanta format i ett pass: 16:9 (YouTube, sajt, TV), 9:16 (Reels, TikTok, Stories), 1:1 (LinkedIn feed), 4:5 (Meta feed). Plus undertexter, kort hooks-cut för paid och thumbnail-bilder för YouTube.",
  },
  {
    q: "Äger vi reklamfilmen efteråt?",
    a: "Ja. Allt slutmaterial är ert att använda fritt i era egna kanaler. Vi behåller rätten att visa utvalda klipp i vår portfolio om inget annat avtalas. Musik och röst clearance ingår — ni får inga oväntade rättighetsfakturor i efterhand.",
  },
  {
    q: "Vad skiljer en bra reklamfilm från en medioker?",
    a: "Tre saker: (1) hook inom 1,5 sekunder — algoritmerna är obarmhärtiga, (2) en specifik insikt om mottagaren — inte generisk varumärkesprosa, (3) en CTA som matchar tratten — awareness ska skapa nyfikenhet, inte tigga klick. Den tekniska kvaliteten är insatsen, inte konkurrensfördelen.",
  },
];

export const Route = createFileRoute("/reklamfilm")({
  head: () => ({
    meta: [
      { title: "Reklamfilm — produktion som levererar | Fenny" },
      {
        name: "description",
        content:
          "Reklamfilm från Fenny — premiumproduktion för paid social, YouTube och TV. Strategi, regi, foto, klipp och versionering. Första klippet inom 10–14 dagar.",
      },
      { property: "og:title", content: "Reklamfilm som funkar — Fenny" },
      { property: "og:description", content: "Premiumreklamfilm för paid social, YouTube och TV. Strategi, produktion och versionering i en process." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/reklamfilm" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/reklamfilm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/reklamfilm", name: "Reklamfilm — produktion som levererar | Fenny", description: "Reklamfilm från Fenny — premiumproduktion för paid social, YouTube och TV. Strategi, regi, foto, klipp och versionering. Första klippet inom 10–14 dagar."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Reklamfilm",
            path: "/reklamfilm",
            serviceType: "Reklamfilmsproduktion",
            description:
              "Premium reklamfilmsproduktion för paid social, YouTube, TV och sajt. Strategi, regi, foto, klipp, grade och formatanpassning i ett pass.",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Reklamfilm", path: "/reklamfilm" },
          ]),
        ),
      },
    ],
  }),
  component: ReklamfilmPage,
});

function ReklamfilmPage() {
  return (
    <LandingPage
      eyebrow="Reklamfilm"
      title={<>Reklamfilm som faktiskt får folk att stanna.</>}
      lede="Vi gör reklamfilm för paid social, YouTube och TV — utan byråspråk och utan månader av väntan. Strategi, regi, foto och versionering i en process. Pulse-kunder får första klippet inom 10–14 dagar; Prime-produktioner går från brief till leverans på 3–6 veckor."
      tldr={<p>Reklamfilm är paid-kommunikation för TV, YouTube, paid social och DOOH med tydligt köp- eller varumärkesmål. Fenny producerar reklamfilm från <strong>80 000 kr</strong> (Prime fastpris, 3–6 veckors ledtid) eller löpande via <strong>Pulse-abonnemang från 14 995 kr/mån</strong> med första klipp inom 10–14 dagar. Inkluderar strategi, regi, post och versionering.</p>}
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="reklamfilm"
      relatedLinksFor="/reklamfilm"
      quickAnswerId="sv_vad_ar_reklamfilm"
      trust={[
        { value: "35–900 kkr", label: "spann social-first till TVC" },
        { value: "3–6 v", label: "Prime-ledtid" },
        { value: "4–6", label: "format per produktion" },
      ]}
      pricing={{
        heading: "Vad reklamfilm kostar 2026",
        intro: "Två tydligt olika produktionslogiker: social-first som optimeras löpande, och kampanjfilm som ska hålla i sex till tolv månader.",
        rows: [
          { format: "Social-first reklamfilm", scope: "1 dag, 4–8 varianter för Meta/TikTok/YouTube, hook-tester", price: "35 000–120 000 kr", lead: "3 veckor" },
          { format: "Kampanjfilm", scope: "1–2 dagar, castade skådespelare, 30 sek + cutdowns", price: "180 000–400 000 kr", lead: "5–7 veckor" },
          { format: "TV-färdig produktion", scope: "2–4 dagar, originalmusik, broadcast-leverans och EBU R128", price: "450 000–900 000 kr", lead: "8–12 veckor" },
        ],
        footnote: "Exkl. moms. Mediebudget, skådespelargager och musiklicens tillkommer och specificeras i offerten.",
      }}
      objections={{
        heading: "Vanliga frågor innan beslut",
        items: [
          { objection: "Behöver vi verkligen en TV-färdig film?", answer: <>Sällan i första steget. De flesta får bättre utväxling på en social-first-batch där vi testar hooks i sex veckor, och därefter skalar upp den vinnande idén.</> },
          { objection: "Vad händer om filmen inte presterar?", answer: <>Vi mäter hook rate och genomförandegrad från vecka ett. I Pulse klipper vi om samma material utan tilläggskostnad; i Prime ingår en omklippsrunda i priset.</> },
        ],
      }}
      bullets={[
        { title: "Hook först", body: "Vi optimerar för algoritmer som dömer på 1,5 sekunder — varje klipp börjar med ett ansikte, en händelse eller en visuell paradox. Inte med er logo." },
        { title: "Versioner ingår", body: "Varje produktion levereras i 4–6 längder och format: 9:16, 1:1, 4:5, 16:9. Plus undertexter och hooks-cut. En insats, hela tratten." },
        { title: "Mätbart från start", body: "Vi sätter målet på första mötet — view-through, CTR eller hand-raisers — och bygger filmen mot det. Inte tvärtom." },
      ]}
      sections={[
        {
          id: "vad-vi-gor",
          heading: "Vad ingår i en reklamfilmsproduktion?",
          body: (
            <>
              <p>
                En reklamfilm från Fenny innehåller hela kedjan: insight-fas och strategi, manus, casting, regi, foto, ljud, klipp, grade, ljudmix och formatversionering. Ni behöver inte upphandla foto, regi, postproduktion och versionering separat — det går till en projektledare som driver hela produktionen mot deadline.
              </p>
              <p>
                För kampanjproduktioner använder vi Prime — ett fast projektpris som täcker allt från första manus till färdiga versioner. För kunder som behöver kontinuerlig reklamfilm månadsvis fungerar Pulse bättre: en fast månadsbudget med 4–20 färdiga filmer per månad beroende på paket.
              </p>
            </>
          ),
        },
        {
          id: "format-och-langd",
          heading: "Vilket format och vilken längd passar?",
          body: (
            <>
              <p>
                För paid social fungerar 9:16 på 9–22 sekunder bäst 2026. För YouTube pre-roll levererar vi i 15 och 30 sekunder. För sajt-hero och employer branding kan långformat på 60–180 sekunder bära mer story. Vi rekommenderar alltid att skära varje produktion i 4–6 längder per insats — annars lämnar ni paid-effekten på bordet.
              </p>
              <p>
                Tumregel: börja en idé så kort som möjligt. Det går alltid att lägga till längd, sällan att ta bort.
              </p>
            </>
          ),
        },
        {
          id: "process",
          heading: "Så går processen till",
          body: (
            <>
              <p>
                Vecka 1: insights och manus. Vecka 2: pre-production, casting och recce. Vecka 3: inspelning (typiskt 1–2 dagar). Vecka 4–5: klipp, grade, ljud och versionering. Leverans i delad mapp, redo att publicera.
              </p>
              <p>
                Pulse-kunder hoppar över de första veckorna — strategin sitter och första klippet är ute inom 10–14 dagar från start.
              </p>
            </>
          ),
        },
        {
          id: "pris",
          heading: "Vad kostar det?",
          body: (
            <p>
              Vi prissätter på effekt och leveransvolym, inte på timmar. Enklare social-first reklamfilm landar typiskt på 35–120 kkr per produktion. TV-färdig kampanj med skådespelare ligger på 250–900 kkr. Pulse-abonnemang från fast månadsbudget om ni vill ha löpande output. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna fram er nivå →</a>
            </p>
          ),
        },
        {
          id: "orter",
          heading: "Reklamfilmsproduktion i hela Sverige",
          body: (
            <p>
              Vi producerar reklamfilm i hela landet med team från våra kontor — utan resepåslag. Läs mer om <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/stockholm">reklamfilm i Stockholm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/goteborg">Göteborg</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/malmo">Malmö</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/karlstad">Karlstad</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/boras">Borås</a>. Vill ni först förstå begreppen? Se <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/fragor/vad-ar-reklamfilm">vad en reklamfilm är</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/vad-kostar-en-reklamfilm">vad den kostar</a>.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseFilter={(c) => c.format?.toLowerCase().includes("brand") || c.category.toLowerCase().includes("brand") || c.service === "Prime"}
      caseLimit={6}
      riskReducers
      leadMagnet={{
        slug: "formatguide-2026",
        title: "Ladda ner: Formatguide 2026",
        description: "Rätt format, rätt kanal, rätt längd — med riktlängder, hook-mönster och en färdig format-matris.",
      }}
      closing={{
        title: "Redo att gå live?",
        body: "30 minuter där vi mappar er nuvarande filminsats mot tre konkreta vinster nästa kvartal.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Räkna pris", to: "/priser" },
      }}
    />
  );
}
